import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getMaterialStateColor } from '../../../../utils/colors';
import { exportTypeEnum, getMaterialStateInfos, getTruckModeInfos, localStorageKeys, materialState, permissionLevels } from '../../../../utils/enums';
import LicensePlate from '../../../LicensePlate';
import GenericAgGridTable from '../../GenericAgGridTable';
import { formatDateToDDMMYYYY, formatDateToYYYYMMDD, formatTime } from '../../../../utils/dates';
import { formatNumber } from '../../../../utils/numbers';
import { useAuth } from '../../../../hooks/useAuth';
import ExportCsvIcon from '../../../ExportCsvIcon';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { stepModeEnum } from '../../../../constants';

interface TrucksRidesTableProps {
    dailyTrucksRides: DailyTruckRide[];
    activities: DataItem[];
    currentDate: Date;
    isLoading: boolean;
    stepMode: number;
}

interface EnrichedTruckRide extends DailyTruckRide {
    siteName: string;
    truckManager: string;
    driverName: string;
    isTruckManager: boolean;
}

const TrucksRidesTable: React.FC<TrucksRidesTableProps> = ({ dailyTrucksRides, activities, currentDate, isLoading, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const rows = useMemo<EnrichedTruckRide[]>(() => {
        if (!dailyTrucksRides.length || !activities.length) return [];
        return dailyTrucksRides
            .map((ride) => {
                const activity = activities.find((a) => a._id === ride.activityId);

                return {
                    ...ride,
                    siteName: activity?.siteId?.name || '',
                    truckManager: activity?.reportId?.userId ? formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName) : '',
                    driverName: formatName(ride.truckId?.userId?.firstName, ride.truckId?.userId?.lastName),
                    isTruckManager: activity?.isTruckManager || false,
                };
            })
            .sort((a, b) => {
                return a.siteName.localeCompare(b.siteName);
            });
    }, [dailyTrucksRides, activities]);

    const columnDefs: ColDef<EnrichedTruckRide>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            colId: 'date',
            initialHide: true,
        },
        {
            field: 'siteName',
            headerName: capitalizeFirstLetter(t('site')),
            initialRowGroup: true,
            initialWidth: 400,
            initialHide: true,
            colId: 'siteName',
        },
        {
            headerName: capitalizeFirstLetter(t('company')),
            valueGetter: (params) => params.data?.truckId?.truckCompanyId?.designation || '',
            colId: 'company',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('plateNumber')),
            field: 'truckId.plateNumber',
            cellRenderer: (params: ICellRendererParams<EnrichedTruckRide>) => {
                if (!params.value) return null;
                return <LicensePlate number={params.value} />;
            },
            colId: 'plateNumber',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('reportOf')),
            field: 'truckManager',
            colId: 'truckManager',
        },
        {
            headerName: capitalizeFirstLetter(t('truckManager')),
            field: 'isTruckManager',
            colId: 'isTruckManager',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            field: 'driverName',
            colId: 'driverName',
        },
        {
            headerName: capitalizeFirstLetter(t('truckType')),
            valueGetter: (params) => params.data?.truckId?.truckTypeId?.designation || '',
            colId: 'truckType',
        },
        {
            headerName: capitalizeFirstLetter(t('mode')),
            valueGetter: (params) => capitalizeFirstLetter(t(getTruckModeInfos(params.data?.mode))),
            colId: 'mode',
        },
        {
            headerName: `${t('source').charAt(0).toUpperCase()}. ${t('loadNature')}`,
            valueGetter: (params) => params.data?.departureLoadNatureId?.designation || '',
            colId: 'departureLoadNature',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0).toUpperCase()}. ${t('location')}`,
            valueGetter: (params) => params.data?.departureSiteId?.name || params.data?.departureQuarryId?.designation || '',
            colId: 'departureLocation',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0).toUpperCase()}. ${t('dump')}`,
            valueGetter: (params) => params.data?.departureDumpId?.designation || '',
            colId: 'departureDump',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0).toUpperCase()}. ${t('tonnage')}`,
            field: 'departureTonnage',
            colId: 'departureTonnage',
            initialAggFunc: 'sum',
            headerStyle: { color: '#00b8c2' },
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return formatNumber(params.value, t('locale'), 2);
            },
        },
        {
            headerName: `${t('source').charAt(0).toUpperCase()}. ${t('hour')}`,
            valueGetter: (params) => formatTime(params.data?.departureTime ? new Date(params.data?.departureTime) : undefined),
            colId: 'departureTime',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: capitalizeFirstLetter(t('siteHour')),
            valueGetter: (params) => formatTime(params.data?.time ? new Date(params.data?.time) : undefined),
            colId: 'time',
        },
        {
            headerName: `${t('destination').charAt(0).toUpperCase()}. ${t('loadNature')}`,
            valueGetter: (params) => params.data?.arrivalLoadNatureId?.designation || '',
            colId: 'arrivalLoadNature',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0).toUpperCase()}. ${t('location')}`,
            valueGetter: (params) => params.data?.arrivalSiteId?.name || params.data?.arrivalQuarryId?.designation || '',
            colId: 'arrivalLocation',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0).toUpperCase()}. ${t('dump')}`,
            valueGetter: (params) => params.data?.arrivalDumpId?.designation || '',
            colId: 'arrivalDump',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0).toUpperCase()}. ${t('tonnage')}`,
            field: 'arrivalTonnage',
            colId: 'arrivalTonnage',
            initialAggFunc: 'sum',
            headerStyle: { color: 'lightgreen' },
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return formatNumber(params.value, t('locale'), 2);
            },
        },
        {
            headerName: `${t('destination').charAt(0).toUpperCase()}. ${t('hour')}`,
            valueGetter: (params) => formatTime(params.data?.arrivalTime ? new Date(params.data?.arrivalTime) : undefined),
            colId: 'arrivalTime',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: capitalizeFirstLetter(t('onSiteBackTime')),
            valueGetter: (params) => formatTime(params.data?.secondTime ? new Date(params.data?.secondTime) : undefined),
            colId: 'secondTime',
        },
        {
            headerName: capitalizeFirstLetter(t('transfers')),
            field: 'transfers',
            valueGetter: (params) =>
                params.data?.transfers.map(
                    (transfer) =>
                        (transfer.machineId?.designation ||
                            transfer.toolId?.designation ||
                            transfer.rentalName + ' ' + transfer.rentalCompanyId?.designation) +
                        ' ' +
                        t(transfer.state === materialState.NEW ? 'from' : 'to') +
                        ' ' +
                        (transfer.siteId?.name || transfer.quarryId?.designation)
                ),
            cellRenderer: (params: ICellRendererParams<EnrichedTruckRide>) => {
                if (!params.data) return null;
                return (
                    <div>
                        {(params.data?.transfers || []).map((transfer: any, i: number) => (
                            <div
                                key={i}
                                className="border-radius-10px pad-l-5px fx-c text-black"
                                style={{
                                    backgroundColor: getMaterialStateColor(transfer.state),
                                    marginTop: i !== 0 ? 5 : 0,
                                }}
                                title={
                                    `${transfer.machineId?.designation || transfer.toolId?.designation || `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}`} ` +
                                    `${t(getMaterialStateInfos(transfer.state))} ` +
                                    `${transfer.siteId?.name || transfer.quarryId?.designation}`
                                }
                            >
                                <div>
                                    {transfer.machineId?.designation ||
                                        transfer.toolId?.designation ||
                                        `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}`}
                                </div>
                                <div>
                                    <b>{transfer.state === materialState.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                    {transfer.siteId?.name || transfer.quarryId?.designation}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
            colId: 'transfers',
        },
        {
            headerName: capitalizeFirstLetter(t('observations')),
            field: 'observations',
            colId: 'observations',
            cellStyle: { whiteSpace: 'pre-wrap' },
        },
        {
            headerName: capitalizeFirstLetter(t('decommissioned')),
            field: 'isDecommissioned',
            colId: 'decommissioned',
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<EnrichedTruckRide>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={rows}
            columnDefs={columnDefs}
            agGridProps={{
                getRowId: ({ data }) => data._id,
                autoGroupColumnDef: {
                    pinned: 'left',
                    initialWidth: 400,
                },

                rowGroupPanelShow: 'always',
                grandTotalRow: 'bottom',
                statusBar: {
                    statusPanels: [
                        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
                        { statusPanel: 'agTotalRowCountComponent' },
                        { statusPanel: 'agFilteredRowCountComponent' },
                        { statusPanel: 'agSelectedRowCountComponent' },
                        { statusPanel: 'agAggregationComponent' },
                    ],
                },
            }}
            loading={isLoading}
            exportCsvFileName={'supervision-trucks-rides-' + formatDateToYYYYMMDD(currentDate)}
            storageKey={localStorageKeys.TABLE.OPERATIONS.SUPERVISION.TRUCKS}
            headerRightComponent={
                <>
                        <ExportCsvIcon
                            initDate={currentDate}
                            onExport={(startDate, endDate, fileName, delimiter) =>
                                exportCsv({
                                    exportType: exportTypeEnum.DRIVERSAMPLIROLL,
                                    queries: { startDate, endDate, delimiter, fileName },
                                })
                            }
                            exportIsLoading={isExportCsvLoading}
                            subTitle={capitalizeFirstLetter(t('machines'))}
                        />
                    
                </>
            }
        />
    );
};

export default TrucksRidesTable;
